/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { DeleteOutlined } from '@ant-design/icons';
import { TableProps } from 'antd';
import { useMemo } from 'react';

import { Node, NodeFilters, PaginatedNodesResponse, Tag } from '@/client';
import { QueryWrapper } from '@/components/QueryWrapper';
import { usePaginatedTagsQuery } from '@/hooks/query/usePaginatedTagsQuery';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { GetNodesColumnsOptions, NodesColumns } from '@/pages/Nodes/getNodesColumns';
import { NodesTable } from '@/pages/Nodes/NodesTable';
import { Button } from '@/primitives/Button';
import { Title } from '@/primitives/Title';
import { tokens } from '@/theme';
import { UpdateParams } from '@/utils/params';

const VISIBLE_COLUMNS: NodesColumns[] = [
  'hostname',
  'appliedOperationalState',
  'appliedNodeConfigMode',
  'connectivityState',
  'operatingSystem',
  'lastSeen',
  'appliedConfiguration',
  'syncStatus',
];

export type NodeGroupNodesTableProps = {
  tenantId: string;
  nodesData: PaginatedNodesResponse;
  onParamsChange: UpdateParams<Node, Partial<NodeFilters>>;
  isEditing: boolean;
  selectedNodeIds: string[];
  onSelectionChange: (nodeIds: string[]) => void;
  onRemoveNodes: () => void;
  isRemovingNodes: boolean;
  columnOptions: GetNodesColumnsOptions;
  getNodeConfigurationPath: (nodeConfigId: string) => string;
};

export function NodeGroupNodesTable({
  tenantId,
  nodesData,
  onParamsChange,
  isEditing,
  selectedNodeIds,
  onSelectionChange,
  onRemoveNodes,
  isRemovingNodes,
  columnOptions,
  getNodeConfigurationPath,
}: NodeGroupNodesTableProps) {
  const { t } = useMimicTranslation('fleets');

  const tagsQuery = usePaginatedTagsQuery({ params: { page: 1, pageSize: 100 } });

  const rowSelection: TableProps<Node>['rowSelection'] = useMemo(
    () =>
      isEditing
        ? {
            type: 'checkbox',
            preserveSelectedRowKeys: true,
            selectedRowKeys: selectedNodeIds,
            onChange: (_selectedKeys: React.Key[], selectedRows: Node[]) => {
              onSelectionChange(selectedRows.map((node) => node.id));
            },
            getCheckboxProps: (node: Node) => ({
              name: node.name,
            }),
          }
        : undefined,
    [isEditing, selectedNodeIds, onSelectionChange],
  );

  const primaryAction = isEditing ? (
    <Button
      dataTestId="remove-nodes"
      type="default"
      disabled={selectedNodeIds.length === 0 || isRemovingNodes}
      loading={isRemovingNodes}
      onClick={onRemoveNodes}
    >
      {selectedNodeIds.length > 0 ? (
        <span style={{ color: tokens.color.red.red07 }}>
          <DeleteOutlined />{' '}
          {selectedNodeIds.length > 1
            ? t('removeNodesCountPlural', { count: selectedNodeIds.length })
            : t('removeNodesCount', { count: selectedNodeIds.length })}
        </span>
      ) : (
        <>
          <DeleteOutlined /> {t('removeNodes')}
        </>
      )}
    </Button>
  ) : undefined;

  return (
    <div style={{ marginTop: '32px', width: '100%' }}>
      <Title level={5} style={{ marginBottom: '16px' }}>
        {t('nodes')}
      </Title>
      <QueryWrapper queryResult={tagsQuery}>
        {(tagsData: { data: Tag[] }) => (
          <NodesTable
            tenantID={tenantId}
            nodesData={nodesData}
            onParamsChange={onParamsChange}
            columns={VISIBLE_COLUMNS}
            tags={tagsData.data}
            getNodeConfigurationPath={getNodeConfigurationPath}
            hideFilters
            columnOptions={columnOptions}
            rowSelection={rowSelection}
            selectedNodeIds={isEditing ? selectedNodeIds : undefined}
            primaryAction={primaryAction}
          />
        )}
      </QueryWrapper>
    </div>
  );
}
